import {
  daysBetweenStages, todasAsEtapasConcluidas, etapaPendente,
} from '../../constants/schedule';

// Prazos da DG60, derivados da mesma régua que o cadastro usa.
//
// Dois porque a DG60 pende da FIV: quando há data de FIV é dela que o prazo
// conta. A contagem pela TE é a reserva para o serviço que não registrou FIV
// (TE avulsa, embrião vindo de fora) — vale a distância canônica entre as duas.
const DG60_APOS_FIV = daysBetweenStages('fiv', 'dg60');
const DG60_APOS_TE = daysBetweenStages('te', 'dg60');

/**
 * Como se chama a espera por cada etapa.
 *
 * Fora da corrente porque não é dado de agenda, é vocabulário de tela: a FIV
 * não é "aguardando FIV", é "no laboratório". Etapa sem verbete aqui cai em
 * "Aguardando <rótulo>", que é feio mas honesto.
 */
const AGUARDANDO = {
  protocolo: 'Aguardando protocolo',
  opu:       'Aguardando OPU',
  fiv:       'No laboratório',
  te:        'Aguardando TE',
  dg30:      'Aguardando conferência',
  dg60:      'Aguardando conferência',
};

export const getServiceStatus = (service) => {
  // Se não tem serviços oferecidos, retorna status genérico
  if (!service.services_offered || service.services_offered.length === 0) {
    return 'Sem etapas definidas';
  }

  // As etapas vêm da corrente, não de um mapa escrito à mão: etapa nova
  // entrava em lugar nenhum e o serviço ficava "Finalizado" com ela em aberto.
  if (todasAsEtapasConcluidas(service)) return 'Finalizado';

  const pendente = etapaPendente(service);
  if (pendente) return AGUARDANDO[pendente.id] || `Aguardando ${pendente.label}`;

  // Caso especial: serviços auxiliares
  if (service.services_offered.includes('embryo_purchase')) {
    return service.te_done ? 'Finalizado' : 'Aguardando transferência';
  }

  if (service.services_offered.includes('daily_service')) {
    // Para diária, considerar finalizado se tem data de conclusão
    return service.work_days ? 'Finalizado' : 'Em andamento';
  }

  return 'Em andamento';
};

/**
 * Dias entre uma data e hoje. Meia-noite dos dois lados para não depender da
 * hora em que a tela foi aberta.
 */
const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const quando = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - quando) / 86400000);
};

/**
 * De onde o prazo da DG60 conta, e qual é ele.
 *
 * A FIV manda quando existe — a idade gestacional começa na fecundação, e a TE
 * pode ter sido remarcada sem que isso adie a prenhez.
 */
const prazoDG60 = (service) => {
  if (service.lab_date) return { desde: service.lab_date, dias: DG60_APOS_FIV };
  if (service.te_date) return { desde: service.te_date, dias: DG60_APOS_TE };
  return null;
};

/**
 * A DG60 está atrasada quando já passou do dia em que ela era esperada.
 *
 * O prazo vem de SCHEDULE_CHAIN em vez de um 60 escrito aqui — era a terceira
 * cópia do mesmo número (cadastro, calendário e este), e se o laboratório
 * mudar a janela da DG60 o alerta tem que acompanhar.
 */
export const isPregnancyCheckOverdue = (service) => {
  if (!service.services_offered?.includes('pregnancy_check')) return false;
  if (service.pregnancy_60d_done) return false;
  // Sem TE feita não há prazo para correr: o embrião ainda nem foi transferido.
  if (!service.te_done) return false;

  const prazo = prazoDG60(service);
  if (!prazo) return false;

  const decorrido = daysSince(prazo.desde);
  return decorrido !== null && decorrido > prazo.dias;
};

/**
 * Calcula quantos dias está atrasada a checagem de prenhez
 */
export const getDaysOverdue = (service) => {
  if (!isPregnancyCheckOverdue(service)) return 0;
  const prazo = prazoDG60(service);
  return daysSince(prazo.desde) - prazo.dias;
};

/**
 * Calcula quantos dias desde que o serviço foi faturado
 */
export const getDaysSinceBilled = (service) => {
  if (!service.billed || !service.billed_date) {
    return 0;
  }

  const billedDate = new Date(service.billed_date);
  const today = new Date();
  const daysSince = Math.floor((today - billedDate) / (1000 * 60 * 60 * 24));

  return daysSince;
};

/**
 * Calcula potencial de faturamento dos serviços atrasados
 * (considerando taxa de sucesso de 45% e apenas serviços "por prenhez")
 */
export const calculateOverduePotentialRevenue = (services) => {
  const SUCCESS_RATE = 0.45;
  let totalPotential = 0;
  let teCount = 0;

  services.forEach(service => {
    if (!isPregnancyCheckOverdue(service)) {
      return;
    }

    // Só considera serviços com modelo de cobrança "por prenhez"
    if (service.billing_model !== 'por_prenhez') {
      return;
    }

    const valuePerPregnancy = parseFloat(service.value_per_pregnancy) || 0;
    const teQuantity = service.te_quantity || 0;

    if (valuePerPregnancy > 0 && teQuantity > 0) {
      // Potencial = TEs × taxa de sucesso × valor por prenhez
      totalPotential += teQuantity * SUCCESS_RATE * valuePerPregnancy;
      teCount += teQuantity;
    }
  });

  return {
    totalPotential,
    teCount,
    successRate: SUCCESS_RATE
  };
};
